import React from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faClipboardList,
    faPlay,
    faStop,
    faClockRotateLeft,
    faRotateRight,
    faUnlockKeyhole,
    faChevronUp,
    faChevronDown,
    faPauseCircle,
} from "@fortawesome/pro-regular-svg-icons";
import { getChargeMonitorButtonStyle } from "../../theme/gitoColors";
import { SESSION_STATUSES, canStartCharging } from "./chargeMonitorHelpers";

const BTN_SHARED =
    "flex items-center justify-center font-semibold border whitespace-nowrap transition bg-[var(--cm-btn-bg)] border-[color:var(--cm-btn-border)] text-[color:var(--cm-btn-text)] hover:bg-[var(--cm-btn-hover)]";
const BTN_GRID =
    `${BTN_SHARED} w-full gap-1.5 px-2.5 h-7 text-xs rounded-md xl:w-auto`;
const BTN_FOOTER =
    `${BTN_SHARED} gap-1.5 px-3 h-8 text-xs rounded-lg`;

const ChargeMonitorActions = ({
    item,
    canManage,
    onAction,
    showDetailsToggle = false,
    detailsExpanded = false,
    layout = "grid",
}) => {
    const { t } = useTranslation();
    const data = item?.Data || {};
    const status = (data.Status || data.chargingConnectorStatus || "").toUpperCase();
    const isOnline = !!data.isOnline;
    const reservationPublicId = data.CurrentReservationPublicId;

    if (!canManage && !SESSION_STATUSES.includes(status)) {
        return null;
    }

    const isFooter = layout === "footer";
    const btnClass = isFooter ? BTN_FOOTER : BTN_GRID;
    const wrapClass = isFooter
        ? "flex flex-wrap justify-end gap-2"
        : "grid w-full grid-cols-2 gap-1.5 md:col-span-1 xl:col-span-2 xl:flex xl:flex-wrap xl:justify-end";

    return (
        <div className={wrapClass}>
            {canManage && (
                <button
                    type="button"
                    onClick={() => onAction("datalog", item)}
                    className={btnClass}
                    style={getChargeMonitorButtonStyle("datalog")}
                >
                    <FontAwesomeIcon icon={faClipboardList} /> {t("chargeMonitor.datalog")}
                </button>
            )}

            {canManage && canStartCharging(status, data.ChargingSiteName) && (
                <button
                    type="button"
                    onClick={() => onAction("start", item)}
                    className={btnClass}
                    style={getChargeMonitorButtonStyle("start")}
                >
                    <FontAwesomeIcon icon={faPlay} /> {t("common.start")}
                </button>
            )}

            {canManage && status === "CHARGING" && (
                <button
                    type="button"
                    onClick={() => onAction("stop", item)}
                    className={btnClass}
                    style={getChargeMonitorButtonStyle("stop")}
                >
                    <FontAwesomeIcon icon={faStop} /> {t("chargeMonitor.stop")}
                </button>
            )}

            {canManage && status !== "CHARGING" && status !== "RESERVED" && isOnline && (
                <button
                    type="button"
                    onClick={() => onAction("availability", item)}
                    className={btnClass}
                    style={getChargeMonitorButtonStyle("availability")}
                >
                    <FontAwesomeIcon icon={faClockRotateLeft} /> {t("chargeMonitor.availability")}
                </button>
            )}

            {canManage && status !== "CHARGING" && isOnline && (
                <button
                    type="button"
                    onClick={() => onAction("restart", item)}
                    className={btnClass}
                    style={getChargeMonitorButtonStyle("restart")}
                >
                    <FontAwesomeIcon icon={faRotateRight} /> {t("common.restart")}
                </button>
            )}

            {canManage && ["CHARGING", "AVAILABLE", "PREPARING", "FINISHING"].includes(status) && isOnline && (
                <button
                    type="button"
                    onClick={() => onAction("unlock", item)}
                    className={btnClass}
                    style={getChargeMonitorButtonStyle("unlock")}
                >
                    <FontAwesomeIcon icon={faUnlockKeyhole} /> {t("common.unlock")}
                </button>
            )}

            {canManage && status === "RESERVED" && isOnline && reservationPublicId && (
                <button
                    type="button"
                    onClick={() => onAction("cancelReservation", item)}
                    className={btnClass}
                    style={getChargeMonitorButtonStyle("stop")}
                >
                    <FontAwesomeIcon icon={faPauseCircle} /> {t("chargeMonitor.cancelReservation")}
                </button>
            )}

            {showDetailsToggle && SESSION_STATUSES.includes(status) && (
                <button
                    type="button"
                    onClick={() => onAction("details", item)}
                    className={btnClass}
                    style={getChargeMonitorButtonStyle("details")}
                >
                    <FontAwesomeIcon icon={detailsExpanded ? faChevronUp : faChevronDown} />
                    {t("common.details")}
                </button>
            )}
        </div>
    );
};

export default ChargeMonitorActions;
